/**
 * Metrics service for owner-only usage analytics.
 *
 * Sends route/session/setup counters only. It does not send amounts,
 * descriptions, account names, category names, or transaction details.
 */

import { db } from './db';
import { supabase } from './supabase';
import { getApiUrl } from './sync';

export type MetricEventType =
  | 'session_start'
  | 'page_view'
  | 'heartbeat'
  | 'setup_snapshot'
  | 'import_completed'
  | 'import_failed';

export interface LocalMetricCounts {
  accounts: number;
  transactions: number;
  categories: number;
  budgets: number;
  scheduledTransactions: number;
  pendingSync: number;
}

export interface ImportMetricSummary {
  success: boolean;
  imported: number;
  skipped: number;
  duplicatesSkipped: number;
  newAccounts: number;
  newCategories: number;
  errorCount?: number;
  warningCount?: number;
  format?: string;
  stage?: string;
  fileSizeKb?: number;
  failureCode?: string;
}

interface TrackOptions {
  counts?: LocalMetricCounts;
  importSummary?: ImportMetricSummary;
}

const DEVICE_ID_KEY = 'ninjacash-metrics-device-id';
const SESSION_ID_KEY = 'ninjacash-metrics-session-id';
const APP_VERSION = 'metrics-dev-v1';
const DEBUG = import.meta.env.VITE_DEBUG === 'true';

function makeId(prefix: string): string {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}:${random}`;
}

export function getMetricsDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = makeId('device');
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

export function getMetricsSessionId(): string {
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = `${getMetricsDeviceId()}:${Date.now()}`;
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}

export function normalizeRoute(pathname: string, search = ''): string {
  const route = `${pathname || '/'}${search || ''}`;
  return route.length > 160 ? route.slice(0, 160) : route;
}

export function getPageTitle(route: string): string {
  const path = route.split('?')[0];
  const titles: Record<string, string> = {
    '/': 'Home',
    '/accounts': 'Accounts',
    '/transactions': 'Transactions',
    '/overview': 'Overview',
    '/categories': 'Categories',
    '/analytics': 'Analytics',
    '/settings': 'Settings',
    '/import': 'Import Data',
    '/dev': 'Dev Tools',
    '/log': 'Logs',
    '/metrics_dev': 'Metrics Dev',
    '/pricing': 'Pricing',
    '/more': 'More',
  };
  return titles[path] || path || 'Unknown';
}

export async function getLocalMetricCounts(): Promise<LocalMetricCounts> {
  const [
    accounts,
    transactions,
    categories,
    budgets,
    scheduledTransactions,
    pendingAccounts,
    pendingTransactions,
    pendingScheduledTransactions,
  ] = await Promise.all([
    db.accounts.count(),
    db.transactions.filter((tx) => !tx.deletedAt).count(),
    db.categories.count(),
    db.budgets.count(),
    db.scheduledTransactions.filter((item) => !item.deletedAt).count(),
    db.accounts.where('syncStatus').equals('pending').count(),
    db.transactions.where('syncStatus').equals('pending').count(),
    db.scheduledTransactions.where('syncStatus').equals('pending').count(),
  ]);

  return {
    accounts,
    transactions,
    categories,
    budgets,
    scheduledTransactions,
    pendingSync: pendingAccounts + pendingTransactions + pendingScheduledTransactions,
  };
}

export function metricCountsHash(counts: LocalMetricCounts): string {
  return [
    counts.accounts,
    counts.transactions,
    counts.categories,
    counts.budgets,
    counts.scheduledTransactions,
    counts.pendingSync,
  ].join(':');
}

async function getAccessToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

export async function trackMetricEvent(
  eventType: MetricEventType,
  route: string,
  options: TrackOptions = {}
): Promise<void> {
  try {
    const token = await getAccessToken();
    if (!token) return;

    const counts = options.counts || await getLocalMetricCounts();
    const body = JSON.stringify({
      eventType,
      route,
      title: getPageTitle(route),
      sessionId: getMetricsSessionId(),
      deviceId: getMetricsDeviceId(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      appVersion: APP_VERSION,
      localCounts: counts,
      importSummary: options.importSummary,
    });

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);

    try {
      await fetch(`${getApiUrl()}/api/metrics/track`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body,
        keepalive: body.length < 60_000,
        signal: controller.signal,
      });
    } finally {
      window.clearTimeout(timeout);
    }
  } catch (err) {
    if (DEBUG) {
      console.warn('[Metrics] Track failed:', err);
    }
  }
}

export async function trackImportCompleted(summary: ImportMetricSummary, route = '/import'): Promise<void> {
  const counts = await getLocalMetricCounts();
  await trackMetricEvent('import_completed', route, {
    counts,
    importSummary: summary,
  });
}

export async function trackImportFailed(summary: ImportMetricSummary, route = '/import'): Promise<void> {
  const counts = await getLocalMetricCounts();
  await trackMetricEvent('import_failed', route, {
    counts,
    importSummary: { ...summary, success: false },
  });
}
